import React from 'react'
import './Policy.css'

function PrivacyPolicy() {
    return (
        <div className="legal-page">
            
            <div className="legal-hero">
                <h1>Privacy Policy</h1>
            </div>

            <div className="breadcrumb">
                <a href="/">Home</a> &gt; Privacy Policy
            </div>

            <div className="legal-content">
                <p className="last-updated">Last Updated: November 10, 2025</p>

                <section className="legal-section">
                    <h2>1. Introduction</h2>
                    <p>
                        Sooru.AI ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                        explains how we collect, use, disclose, and safeguard your information when you use our platform 
                        and services. Please read this privacy policy carefully. If you do not agree with the terms of 
                        this privacy policy, please do not access the platform.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>2. Information We Collect</h2>
                    <p>
                        We collect information that you provide directly to us, including:
                    </p>
                    <h3>2.1 Personal Information</h3>
                    <ul>
                        <li>Name, email address, and phone number</li>
                        <li>Account credentials and profile information</li>
                        <li>Payment and billing information</li>
                        <li>Professional information such as company name and role</li>
                    </ul>
                    <h3>2.2 Usage Information</h3>
                    <ul>
                        <li>Information about how you use our platform</li>
                        <li>Device information, IP address, and browser type</li>
                        <li>Cookies and similar tracking technologies</li>
                        <li>Log data and analytics information</li>
                    </ul>
                    <h3>2.3 Design and Project Information</h3>
                    <ul>
                        <li>Architectural designs and floor plans you create</li>
                        <li>Project specifications and documentation</li>
                        <li>Files and content you upload to the platform</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>3. How We Use Your Information</h2>
                    <p>
                        We use the information we collect for various purposes, including:
                    </p>
                    <ul>
                        <li>Providing, maintaining, and improving our services</li>
                        <li>Processing your transactions and managing your account</li>
                        <li>Sending you technical notices, updates, and support messages</li>
                        <li>Responding to your comments, questions, and customer service requests</li>
                        <li>Developing new features and services</li>
                        <li>Monitoring and analyzing trends, usage, and activities</li>
                        <li>Detecting, preventing, and addressing technical issues and fraudulent activity</li>
                        <li>Personalizing your experience on our platform</li>
                        <li>Sending marketing communications (with your consent)</li>
                    </ul>
                </section>

                <section className="legal-section">
                    <h2>4. Information Sharing and Disclosure</h2>
                    <p>
                        We do not sell your personal information. We may share your information in the following circumstances:
                    </p>
                    <h3>4.1 Service Providers</h3>
                    <p>
                        We may share your information with third-party service providers who perform services on our behalf, 
                        such as payment processing, data analysis, email delivery, hosting services, and customer service.
                    </p>
                    <h3>4.2 Legal Requirements</h3>
                    <p>
                        We may disclose your information if required to do so by law or in response to valid requests by 
                        public authorities (e.g., a court or government agency).
                    </p>
                    <h3>4.3 Business Transfers</h3>
                    <p>
                        In connection with any merger, sale of company assets, financing, or acquisition of all or a portion 
                        of our business, your information may be transferred to the acquiring entity.
                    </p>
                    <h3>4.4 With Your Consent</h3>
                    <p>
                        We may share your information for any other purpose with your explicit consent.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>5. Data Security</h2>
                    <p>
                        We implement appropriate technical and organizational security measures to protect your personal 
                        information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                    </p>
                    <ul>
                        <li>Encryption of data in transit and at rest</li>
                        <li>Regular security assessments and audits</li>
                        <li>Access controls and authentication mechanisms</li>
                        <li>Secure data storage and backup procedures</li>
                    </ul>
                    <p>
                        However, no method of transmission over the Internet or electronic storage is 100% secure. While we 
                        strive to protect your personal information, we cannot guarantee its absolute security.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>6. Data Retention</h2>
                    <p>
                        We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
                        Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer 
                        need your information, we will securely delete or anonymize it.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>7. Your Rights and Choices</h2>
                    <p>
                        Depending on your location, you may have certain rights regarding your personal information:
                    </p>
                    <ul>
                        <li><strong>Access:</strong> You can request access to the personal information we hold about you</li>
                        <li><strong>Correction:</strong> You can request that we correct inaccurate or incomplete information</li>
                        <li><strong>Deletion:</strong> You can request deletion of your personal information</li>
                        <li><strong>Opt-out:</strong> You can opt-out of receiving marketing communications</li>
                        <li><strong>Data Portability:</strong> You can request a copy of your data in a portable format</li>
                        <li><strong>Withdrawal of Consent:</strong> You can withdraw consent for processing based on consent</li>
                    </ul>
                    <p>
                        To exercise these rights, please contact us at <a href="mailto:info@sooru.ai">info@sooru.ai</a>.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>8. Cookies and Tracking Technologies</h2>
                    <p>
                        We use cookies and similar tracking technologies to track activity on our platform and hold certain 
                        information. Cookies are files with small amounts of data that may include an anonymous unique identifier.
                    </p>
                    <p>
                        You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, 
                        if you do not accept cookies, you may not be able to use some portions of our platform.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>9. Third-Party Links</h2>
                    <p>
                        Our platform may contain links to third-party websites or services. We are not responsible for the 
                        privacy practices of these third parties. We encourage you to read the privacy policies of any 
                        third-party sites you visit.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>10. Children's Privacy</h2>
                    <p>
                        Our platform is not intended for use by children under the age of 18. We do not knowingly collect 
                        personal information from children under 18. If you become aware that a child has provided us with 
                        personal information, please contact us, and we will take steps to delete such information.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>11. International Data Transfers</h2>
                    <p>
                        Your information may be transferred to and maintained on computers located outside of your state, 
                        province, country, or other governmental jurisdiction where data protection laws may differ. We take 
                        appropriate measures to ensure your data is treated securely and in accordance with this Privacy Policy.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>12. Changes to This Privacy Policy</h2>
                    <p>
                        We may update our Privacy Policy from time to time. We will notify you of any changes by posting the 
                        new Privacy Policy on this page with an updated "Last Updated" date. You are advised to review this 
                        Privacy Policy periodically for any changes.
                    </p>
                </section>

                <section className="legal-section">
                    <h2>13. Contact Us</h2>
                    <p>
                        If you have any questions about this Privacy Policy or our data practices, please contact us at:
                    </p>
                    <p>
                        <strong>Email:</strong> <a href="mailto:info@sooru.ai">info@sooru.ai</a><br />
                        <strong>Phone:</strong> +91 97438 10910<br />
                        <strong>Address:</strong> No. 816, 27th Main Road, Sector - 1, H S R Layout, Bengaluru 560 102
                    </p>
                </section>
            </div>
        </div>
    )
}

export default PrivacyPolicy